import React, { useCallback, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Button
} from "@/components";
import { MapLocationSelector } from "./map-location-selector";
import type { MapLocation } from "./map-location-selector";

export interface MapLocationDialogProps {
  onLocationSelect: (location: MapLocation) => void;
  trigger?: React.ReactNode;
  defaultCenter?: { lat: number; lng: number };
  initialLocation?: MapLocation | null;
  disabled?: boolean;
}

export const MapLocationDialog: React.FC<MapLocationDialogProps> = ({
  onLocationSelect,
  trigger,
  defaultCenter,
  initialLocation,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleLocationSelect = useCallback(
    (location: MapLocation) => {
      onLocationSelect(location);
      setIsOpen(false);
    },
    [onLocationSelect]
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const defaultTrigger = useMemo(
    () => (
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-12 w-12 shrink-0 self-start"
        disabled={disabled}
        aria-label="Select location on map"
      >
        <svg
          className="h-4 w-4 text-red-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
          />
        </svg>
      </Button>
    ),
    [disabled]
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[150vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Select Location on Map</DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <MapLocationSelector
            center={defaultCenter}
            onLocationSelect={handleLocationSelect}
            onClose={handleClose}
            initialLocation={initialLocation}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
